import { useState, useMemo } from 'react';
import { PROJECT_STATES_OPTIONS, PROJECT_SOURCE_OPTIONS } from '../../constants/constants';
import { useGetClientsList, useGetProjectsUsersList } from '../../utils/projects-list.utils';
import useProjects from '../../hooks/useProjects';
import useProjectGroups from '../../hooks/useProjectGroups';
import useCompanies from '../../hooks/useCompanies';
import useContacts from '../../hooks/useContacts';
import useUsers from '../../hooks/useUsers';
import { useAuth } from '../../context/AuthContext';

export const useGetProjectListFiltersData = () => {
  const [clientsList, setClientsList] = useState([]);

  useGetClientsList(setClientsList);
  const { leadsList = [], isLeadsLoading } = useGetProjectsUsersList();

  const { data: projectGroupsData, isLoading: isProjectGroupsLoading } = useProjectGroups(false);

  const sortedClientsList = useMemo(
    () => (clientsList || []).sort((a, b) => a.label.localeCompare(b.label)),
    [clientsList],
  );

  const sortedLeadsList = useMemo(
    () => (leadsList || []).sort((a, b) => a.label.localeCompare(b.label)),
    [leadsList],
  );

  // const sortedStateList = useMemo(
  //   () => PROJECT_STATES_OPTIONS.sort((a, b) => a.label.localeCompare(b.label)),
  //   []
  // );

  const projectGroups = useMemo(() => {
    if (!projectGroupsData) return [];
    return projectGroupsData
      .map((group) => ({
        label: group.projectName,
        value: group.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [projectGroupsData]);

  const options = useMemo(
    () => [
      {
        key: 'group',
        name: 'Project Group',
        options: [...projectGroups],
        color: '#AA7BE0',
      },
      {
        key: 'client',
        name: 'Client',
        options: [...sortedClientsList],
        color: '#FFDCAD',
      },
      {
        key: 'lead',
        name: 'Project Lead',
        options: [...sortedLeadsList],
        color: '#BDB5F8',
      },
      {
        key: 'state',
        name: 'State',
        options: [...PROJECT_STATES_OPTIONS],
        color: '#E4F9DC',
      },
      {
        key: 'source',
        name: 'Source',
        options: [...PROJECT_SOURCE_OPTIONS],
        color: '#FBD1C7',
      },
    ],
    [projectGroups, sortedClientsList, sortedLeadsList],
  );

  return { options, isLoading: isLeadsLoading || isProjectGroupsLoading };
};

export const useGetProjectGroupOptions = () => {
  const [clientsList, setCLientsList] = useState([]);

  useGetClientsList(setCLientsList);
  const { leadsList, isLeadsLoading } = useGetProjectsUsersList();

  const sortedClientsList = clientsList.sort((a, b) => a.label.localeCompare(b.label));

  const sortedLeadsList = leadsList.sort((a, b) => a.label.localeCompare(b.label));

  const options = [
    {
      key: 'client',
      name: 'Client',
      options: [...sortedClientsList],
      color: '#FFDCAD',
    },
    {
      key: 'lead',
      name: 'Project Group Lead',
      options: [...sortedLeadsList],
      color: '#BDB5F8',
    },
  ];

  return { options, isLoading: isLeadsLoading };
};

export const useGetCompaniesFiltersData = () => {
  const { companies, isLoading } = useCompanies();

  const options = useMemo(() => {
    return [
      {
        key: 'type',
        name: 'Type',
        options: [
          { label: 'Client', value: 'Client' },
          { label: 'Vendor', value: 'Vendor' },
        ],
        color: '#FFDCAD',
      },
      {
        key: 'companyState',
        name: 'State',
        options: [
          { label: 'Active', value: 'Active' },
          { label: 'Inactive', value: 'Inactive' },
        ],
        color: '#BDB5F8',
      },
    ];
  }, []);

  return { options, isLoading };
};

export const useGetContactsFiltersData = () => {
  const { companies, isLoading: isCompaniesLoading } = useCompanies();
  const { contacts, isLoading: isContactsLoading } = useContacts();

  const isLoading = isCompaniesLoading || isContactsLoading;

  const options = useMemo(() => {
    if (!companies || !contacts) return [];

    // Company Options
    const companyOptions = companies
      .map(({ companyID, companyName }) => ({
        label: companyName || 'Unknown Company',
        value: companyID,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Type Options
    const contactTypes = [...new Set(contacts.map((contact) => contact.type))].map((type) => ({
      label: type,
      value: type,
    }));

    return [
      {
        key: 'type',
        name: 'Type',
        options: contactTypes,
        color: '#BDB5F8',
      },
      {
        key: 'company',
        name: 'Company',
        options: companyOptions,
        color: '#FFDCAD',
      },
      {
        key: 'contactState',
        name: 'State',
        options: [
          { label: 'Active', value: 'Active' },
          { label: 'Inactive', value: 'Inactive' },
        ],
        color: '#E4F9DC',
      },
    ];
  }, [companies, contacts]);

  return { options, isLoading };
};

export const useGetForecastData = () => {
  const { data: fetchedProjects = [], isLoading } = useProjects();
  const { userDetails, userPermissions } = useAuth();
  const [clientsList, setClientsList] = useState([]);
  const { data: projectGroupsData, isLoading: isProjectGroupsLoading } = useProjectGroups();

  useGetClientsList(setClientsList, { excludeInactive: true });

  const userFilteredProjects = useMemo(() => {
    if (!userDetails || !userPermissions) return [];

    if (userPermissions === 'Admin') {
      return fetchedProjects;
    } else if (userPermissions === 'Project Manager' || userPermissions === 'Supervisor') {
      if (userDetails?.email === 'lkho@fka.agency') {
        const authorizedLeads = ['Larissa Kho', 'Larissa Y Kho'];
        return fetchedProjects.filter((project) => authorizedLeads.includes(project.projectLead));
      }
      return fetchedProjects.filter((project) => project.projectLead === userDetails?.fullName);
    }

    return [];
  }, [fetchedProjects, userDetails, userPermissions]);

  // Generate filter options
  const options = useMemo(() => {
    const probabilityOptions = [
      { label: '100%', value: '100' },
      { label: '80%', value: '80' },
      { label: '60%', value: '60' },
      { label: '40%', value: '40' },
      { label: '20%', value: '20' },
    ];

    const projectStateOptions = [
      { label: 'Opportunity', value: 'Opportunity' },
      { label: 'Committed', value: 'Committed' },
      { label: 'Active', value: 'Active' },
      { label: 'Complete', value: 'Complete' },
    ];

    const clientOptions = clientsList
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((client) => ({
        label: client.label,
        value: client.value,
      }));

    const projectGroupOptions = projectGroupsData
      ?.map((group) => ({
        label: group.projectName,
        value: group.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [
      {
        key: 'state',
        name: 'State',
        options: projectStateOptions,
        color: '#BDB5F8',
      },
      {
        key: 'probability',
        name: 'Probability',
        options: probabilityOptions,
        color: '#E4F9DC',
      },
      // {
      //   key: "group",
      //   name: "Project Group",
      //   options: projectGroupOptions,
      //   default: "all",
      //   color: "#AA7BE0",
      // },
      {
        key: 'client',
        name: 'Client',
        options: clientOptions,
        color: '#B5EAD7',
        isSelectAll: true,
      },
    ];
  }, [clientsList, projectGroupsData]);

  return { userFilteredProjects, options, isLoading, clientsList };
};

export const useGetUserFiltersData = () => {
  const { data: users = [], isLoading } = useUsers();

  const options = useMemo(() => {
    const uniqueValues = (key) =>
      Array.from(new Set(users.map((user) => user[key]))).map((value) => ({
        label: value,
        value,
      }));

    return [
      {
        key: 'employmentStatus',
        name: 'Employment Status',
        options: uniqueValues('employmentStatus'),
        color: '#FFDCAD',
      },
      {
        key: 'permissions',
        name: 'Permissions',
        options: uniqueValues('permissions'),
        color: '#BDB5F8',
      },
      {
        key: 'serviceGroup',
        name: 'Service Group',
        options: uniqueValues('serviceGroup'),
        color: '#E4F9DC',
      },
    ];
  }, [users]);

  return { options, isLoading };
};
